// Import the SpeechProviderInterface, which extends EventEmitter and provides base method contracts
import SpeechProviderInterface from '../SpeechProviderInterface';

/**
 * DeepgramTranscriberProvider integrates with Deepgram’s real-time WebSocket API.
 * It uses the browser's MediaRecorder to stream microphone audio to Deepgram.
 * Events emitted: 'start', 'transcript', 'error', 'stop'
 */
export default class DeepgramTranscriberProvider extends SpeechProviderInterface {
    constructor({ apiKey, punctuate = true, interim = true, debug = false, fillerWords = true, smartFormat = true } = {}) {
        super();

        if (!apiKey) throw new Error('Missing Deepgram API key');

        this.apiKey = apiKey;
        this.punctuate = punctuate;
        this.interimResults = interim;
        this.debug = debug;
        this.fillerWords = fillerWords;
        this.smartFormat = smartFormat;

        this.socket = null;        // WebSocket connection to Deepgram
        this.mediaStream = null;   // Audio stream from user's microphone
        this.recorder = null;      // MediaRecorder instance
        this.started = false;      // Whether recording has started
    }

    /**
     * Establish WebSocket connection to Deepgram API and stream audio
     */
    connectSocket() {
        const params = new URLSearchParams({
            punctuate: String(this.punctuate),
            interim_results: String(this.interimResults),
            filler_words: String(this.fillerWords),
            smart_format: String(this.smartFormat),
            no_delay: "true"
        });
        const url = `wss://api.deepgram.com/v1/listen?${params.toString()}`;
        this.socket = new WebSocket(url, ['token', this.apiKey]);

        // On WebSocket connection open
        this.socket.onopen = () => {
            if (!this.mediaStream || this.started) return;

            try {
                // Initialize MediaRecorder with the audio stream
                this.recorder = new MediaRecorder(this.mediaStream);

                // Send audio data to Deepgram whenever available
                this.recorder.ondataavailable = ({ data }) => {
                    try {
                        if (
                            data &&
                            data.size > 0 &&
                            this.socket &&
                            this.socket.readyState === WebSocket.OPEN
                        ) {
                            this.socket.send(data);
                        }
                    } catch (err) {
                        this.debug && console.error('[Deepgram] send error:', err);
                    }
                };

                this.recorder.start(250); // Send data in 250ms chunks
                this.started = true;
                this.emit('start'); // Emit 'start' when recording begins
            } catch (err) {
                this.debug && console.error('[Deepgram] MediaRecorder error:', err);
                this.emit('error', err);
                this.emit('stop');
            }
        };

        // On receiving message (transcription result)
        this.socket.onmessage = ({ data }) => {
            try {
                const json = JSON.parse(data);

                // Extract interim & final transcripts
                const interimTranscript = json?.channel?.alternatives?.[0]?.transcript || "";
                const isFinal = json?.is_final || false;

                // Emit interim transcript if available
                if (interimTranscript.trim()) {
                    this.emit('transcript-interim', interimTranscript.trim());
                    this.emit('listening', true); // actively speaking
                }

                // Emit final transcript + silence detection
                if (isFinal && interimTranscript.trim()) {
                    this.emit('transcript', interimTranscript.trim());
                    this.emit('listening', false); // silence detected
                }
            } catch (err) {
                console.error('[Deepgram] JSON parse error:', err);
            }
        };

        // On WebSocket error
        this.socket.onerror = (err) => {
            this.debug && console.error('[Deepgram] WebSocket error:', err);
            this.emit('error', err);
        };

        // On WebSocket close
        this.socket.onclose = () => {
            this.debug && console.log('[Deepgram] WebSocket closed');
        };
    }

    /**
     * Starts the transcription process by requesting microphone access and connecting the WebSocket
     */
    async start() {
        if (this.started) return;

        try {
            this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            this.connectSocket();
        } catch (err) {
            this.emit('error', err);
            this.emit('stop');
        }
    }

    /**
     * Stops the transcription process, closes all media and WebSocket resources
     */
    stop() {
        if (this.recorder && this.recorder.state !== 'inactive') {
            this.recorder.stop();
        }
        this.recorder = null;

        if (this.mediaStream) {
            this.mediaStream.getTracks().forEach((track) => track.stop());
            this.mediaStream = null;
        }

        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            this.socket.close();
        }
        this.socket = null;

        this.started = false;
        this.emit('stop'); // Emit 'stop' when everything is cleaned up
    }

    /**
     * Returns a status identifier
     */
    getStatus() {
        return 'deepgram';
    }
};
